import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSearch } from "../../../contexts/SearchContext";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import { useFlightOfferUi } from "../../../contexts/FlightOfferUiContext";
import { useBookingFlow } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/hooks/useBookingFlow";
import { useFlightRealProposalSubmit } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/context/FlightRealProposalSubmitContext";
import { applyResolvedFlightWizardBack } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/wizard/flightWizardBackStep";
import {
  applyHandlingFeeChange,
  bindDetailsQuoteAction,
  buildDetailsWave1bPresentation,
  QUOTE_ACTION_UI_GAP,
} from "../bridge/detailsWave1b";
import FlightDetailsScreen from "./FlightDetailsScreen";

/**
 * Details container. Contexts stay here. FlightDetailsScreen receives
 * ViewModels and callbacks only. Reprice stays on Results.
 */
export default function FlightDetailsWave1bLive() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams();
  const { setFlightSearchEditorOpen } = useFlightOfferUi();
  const {
    selectedFlightOffer,
    pricing,
    nextStep,
    goToStep,
    canAdvanceFromCurrentStep,
    recomputePricingNow,
    currentStep,
    searchGate,
  } = useBookingFlow();
  const {
    origin,
    location,
    calendarDateArrival,
    calendarDateLeave,
    roomInfo,
    flightTripType,
  } = useSearch();
  const { handleFeeData, updateHandleFeeData } = useSelectedResult();
  const { createDraftFromFlightDetails } = useFlightRealProposalSubmit();

  const presentation = useMemo(
    () =>
      buildDetailsWave1bPresentation({
        selectedFlightOffer,
        pricing,
        bookingExchangeRateSnapshot: null,
        searchFields: {
          origin,
          location,
          calendarDateArrival,
          calendarDateLeave,
          roomInfo,
          flightTripType,
        },
        handleFeeData,
        canAdvance: canAdvanceFromCurrentStep(),
      }),
    [
      selectedFlightOffer,
      pricing,
      origin,
      location,
      calendarDateArrival,
      calendarDateLeave,
      roomInfo,
      flightTripType,
      handleFeeData,
      canAdvanceFromCurrentStep,
    ]
  );

  const onHandlingFeeChange = useCallback(
    (payload) => {
      applyHandlingFeeChange(
        { updateHandleFeeData, recomputePricingNow },
        payload
      );
    },
    [updateHandleFeeData, recomputePricingNow]
  );

  const onBack = useCallback(() => {
    applyResolvedFlightWizardBack({
      currentStep,
      selectedFlightOffer,
      searchGate,
      goToStep,
      navigate,
      pathname,
      routeParams,
      setFlightSearchEditorOpen,
    });
  }, [
    currentStep,
    selectedFlightOffer,
    searchGate,
    goToStep,
    navigate,
    pathname,
    routeParams,
    setFlightSearchEditorOpen,
  ]);

  const onContinue = useCallback(() => {
    nextStep();
  }, [nextStep]);

  // QUOTE_ACTION_UI_GAP — existing createDraftFromFlightDetails, no greenfield control.
  const onCreateQuote = bindDetailsQuoteAction({
    canCreateQuote: presentation.quote?.canCreateQuote,
    createDraftFromFlightDetails,
  });
  void onCreateQuote;
  void QUOTE_ACTION_UI_GAP;

  return (
    <FlightDetailsScreen
      offer={presentation.offer}
      search={presentation.search}
      showReprice={false}
      onContinue={onContinue}
      onBack={onBack}
      continueLabel={presentation.continueLabel}
      backLabel="חזרה לתוצאות"
      continueDisabled={!canAdvanceFromCurrentStep()}
      handlingFee={presentation.handlingFee}
      onHandlingFeeChange={onHandlingFeeChange}
    />
  );
}
